// The first time the board shows a theme's picture, it reads the picture's
// own colors: its tone where the tiles do not cover it, which keeps text
// readable over it, and, for a theme that left its colors to the picture, the
// whole palette. Then it saves them, once, for every board that wears it.
//
// A theme an agent drew comes as an SVG, and the first board to show it turns
// the drawing into its picture: drawn once at screen size, or, for pixel art,
// pixel by pixel and scaled up by a whole number, so every pixel stays sharp.
// From then on it is a picture theme like any other, and the drawing is kept
// as its source.

import type { CustomTheme } from "../../shared/types";
import { apiUpload } from "../lib/api";
import { get } from "../state/store";
import { replaceTheme, specOf } from "../state/themes";
import { backgroundUrl } from "./backgrounds";
import { lookFromPixels, type PictureLook } from "./palette";

const done = new Set<string>();

export async function measureTheme(t: CustomTheme | null | undefined): Promise<void> {
  if (!t) return;
  if (t.drawing && !t.backgrounds?.length) return finishDrawing(t);
  const first = t.backgrounds?.[0];
  if (!first || (!t.auto && first.tone)) return;
  const key = `${t.id}:${t.updatedAt}`;
  if (done.has(key)) return;
  done.add(key);
  try {
    const img = new Image();
    img.decoding = "async";
    img.src = backgroundUrl(first.key);
    await img.decode();
    const look = lookOf(img, img.naturalWidth, img.naturalHeight);
    // the theme may have changed while the picture loaded; then the newer one is measured in turn
    const now = get().themes[t.id];
    if (!now || now.updatedAt !== t.updatedAt) return;
    await replaceTheme(
      t.id,
      {
        ...specOf(t),
        ...(t.auto ? { mode: look.mode, colors: look.colors, glass: t.glass ?? 0.22, blur: t.blur ?? 18 } : {}),
        backgrounds: [{ ...first, tone: look.tone }, ...(t.backgrounds ?? []).slice(1)],
      },
      t.updatedAt,
    );
  } catch {
    // the picture could not be read; the theme keeps the colors it has
  }
}

/** A picture's colors, from a small copy of it. */
function lookOf(src: CanvasImageSource, w0: number, h0: number): PictureLook {
  const w = 96;
  const h = Math.max(24, Math.round((96 * h0) / Math.max(1, w0)));
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  const ctx = c.getContext("2d", { willReadFrequently: true })!;
  ctx.drawImage(src, 0, 0, w, h);
  return lookFromPixels(ctx.getImageData(0, 0, w, h).data, w, h);
}

/** The drawing's own size, from its viewBox or its width and height; a screen's shape when it says neither. */
function boxOf(svg: string): { w: number; h: number } {
  const root = /<svg\b[^>]*>/i.exec(svg)?.[0] ?? "";
  const vb = /viewBox\s*=\s*["']\s*[-\d.]+[\s,]+[-\d.]+[\s,]+([\d.]+)[\s,]+([\d.]+)/i.exec(root);
  if (vb && Number(vb[1]) > 0 && Number(vb[2]) > 0) return { w: Number(vb[1]), h: Number(vb[2]) };
  const w = Number(/\swidth\s*=\s*["']?([\d.]+)/i.exec(root)?.[1]);
  const h = Number(/\sheight\s*=\s*["']?([\d.]+)/i.exec(root)?.[1]);
  return w > 0 && h > 0 ? { w, h } : { w: 1600, h: 900 };
}

/**
 * The drawing as it is drawn: at the size asked, covering it the way a screen's
 * picture covers the screen, and, for pixel art, with its edges hard.
 */
function sized(svg: string, w: number, h: number, box: { w: number; h: number }, pixel: boolean): string {
  return svg.replace(/<svg\b[^>]*>/i, (root) => {
    let r = root.replace(/\s(width|height|preserveAspectRatio|shape-rendering)\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "");
    if (!/viewBox\s*=/i.test(r)) r = r.replace(/<svg\b/i, `<svg viewBox="0 0 ${box.w} ${box.h}"`);
    return r.replace(/<svg\b/i, `<svg width="${w}" height="${h}" preserveAspectRatio="xMidYMid slice"${pixel ? ' shape-rendering="crispEdges"' : ""}`);
  });
}

async function finishDrawing(t: CustomTheme): Promise<void> {
  const key = `${t.id}:${t.updatedAt}:drawing`;
  if (done.has(key)) return;
  done.add(key);
  const box = boxOf(t.drawing!);
  const pixel = !!t.pixel;
  // pixel art: as many pixels across as the drawing is units wide, within reason; then whole-number scaled
  const pw = pixel ? Math.round(Math.min(640, Math.max(96, box.w <= 640 ? box.w : 320))) : 2560;
  const ph = Math.round((pw * 9) / 16);
  const url = URL.createObjectURL(new Blob([sized(t.drawing!, pw, ph, box, pixel)], { type: "image/svg+xml" }));
  try {
    const img = new Image();
    img.decoding = "async";
    img.src = url;
    await img.decode();
    const art = document.createElement("canvas");
    art.width = pw;
    art.height = ph;
    const actx = art.getContext("2d")!;
    actx.imageSmoothingEnabled = !pixel;
    actx.drawImage(img, 0, 0, pw, ph);
    let out = art;
    if (pixel) {
      const k = Math.max(2, Math.ceil(1920 / pw));
      out = document.createElement("canvas");
      out.width = pw * k;
      out.height = ph * k;
      const octx = out.getContext("2d")!;
      octx.imageSmoothingEnabled = false;
      octx.drawImage(art, 0, 0, out.width, out.height);
    }
    const look = lookOf(art, pw, ph);
    const blob = await new Promise<Blob>((ok, no) => out.toBlob((b) => (b ? ok(b) : no(new Error("The drawing could not be prepared."))), pixel ? "image/png" : "image/jpeg", 0.9));
    const { background } = await apiUpload<{ background: { key: string; w: number; h: number } }>("/api/images", blob);
    const now = get().themes[t.id];
    if (!now || now.updatedAt !== t.updatedAt) return;
    await replaceTheme(
      t.id,
      {
        ...specOf(t),
        ...(t.auto ? { mode: look.mode, colors: look.colors, glass: t.glass ?? 0.22, blur: t.blur ?? 18 } : {}),
        backgrounds: [{ ...background, tone: look.tone }],
      },
      t.updatedAt,
    );
  } catch {
    // a drawing that cannot be drawn leaves the theme on its scene and colors
  } finally {
    URL.revokeObjectURL(url);
  }
}
